import { useEffect, useState, useCallback, useRef } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Modal } from '@/components/ui/modal';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ChevronLeft,
  ChevronRight,
  RotateCw,
  ExternalLink,
  Copy,
  Check,
  Plus,
  X,
  Pencil,
} from 'lucide-react';
import {
  adminApi,
  OrgBillingSummary,
  ProductBillingEntry,
  EmployeeBillingEntry,
  BillingInvoiceSummary,
  CreateInvoiceLineItemRequest,
  AdminEmployee,
  UpdateEmployeeSubscriptionRequest,
  GrantHistoryItem,
  AdminCreateDealResponse,
  AdminProductSubscription,
} from '../services/adminApi';
// Compact token formatter ("10.0M") used for the live "= X" echoes in the deal/grant dialogs.
// Distinct from the local formatTokens() below, which appends " tokens".
import { formatTokens as formatTokenCount } from './OrganizationsTab';
import { getOrgDetailUrl } from './adminNavigation';
import { getProductName } from './productCatalog';
import { formatDate } from './formatDate';

interface OrgBillingTabProps {
  orgId: string;
  /**
   * One-shot signal from the Stripe card-collection round-trip (see OrgDetailPage). When the rep
   * returns from hosted Checkout the URL carries ?card=collected|cancelled; OrgDetailPage parses
   * it, opens this tab, and forwards the result here so we can confirm it inline. The billing
   * summary already re-fetches on mount, so a fresh card-on-file is reflected automatically.
   */
  cardCollectionResult?: 'collected' | 'cancelled' | null;
  cardCollectionReturnPath?: string;
}

// Map the Fluent Badge color names still returned by the get*Color helpers below onto shadcn
// Badge classes, preserving the status color SEMANTICS after the migration:
// success = emerald, warning = amber, danger = red, informative/subtle = neutral gray,
// brand = accent (blue). Hues mirror aieweb's ProductSubscriptionRow chips.
const BADGE_CLASS: Record<string, string> = {
  success: 'border-transparent bg-gold/15 text-foreground',
  warning: 'border-transparent bg-warning/10 text-warning',
  danger: 'border-transparent bg-destructive/10 text-destructive',
  brand: 'border-transparent bg-secondary text-foreground',
  informative: 'border-transparent bg-muted text-text-secondary',
  subtle: 'border-transparent bg-muted text-muted-foreground',
};

function badgeClass(color: string): string {
  return BADGE_CLASS[color] || BADGE_CLASS.informative;
}

function CopyableId({ value, label }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <span className="copyable-id" title={value}>
      {label && <span className="copyable-id-label">{label}: </span>}
      <code>{value.length > 20 ? value.slice(0, 8) + '...' + value.slice(-8) : value}</code>
      <button className="copy-btn" onClick={handleCopy} title="Copy to clipboard">
        {copied ? <Check /> : <Copy />}
      </button>
    </span>
  );
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}

// Round a dollar amount to whole cents, half AWAY FROM ZERO, matching the server's pinned
// MidpointRounding.AwayFromZero so the dialog's displayed total always equals the charged total —
// including at exact half-cent midpoints (e.g. $416.665 → $416.67). The +1e-6 (on a cents scale)
// absorbs binary-float representation error (416.665*100 lands at 41666.49999996) without ever
// reaching the next distinct cent, so it never flips a legitimately-below-midpoint value.
export function roundToCents(amount: number): number {
  const scaledCents = amount * 100;
  const rounded = scaledCents >= 0
    ? Math.floor(scaledCents + 0.5 + 1e-6)
    : Math.ceil(scaledCents - 0.5 - 1e-6);
  return rounded / 100;
}

// The full prepay total charged at creation: round the monthly rate to cents FIRST, then multiply
// by the term (per-month rounding first — the server does the same), then re-round to shed any
// float dust from the multiply. term=0/NaN collapses to a single month.
export function computePrepaidTotal(monthlyFee: number, termMonths: number): number {
  const months = Number.isFinite(termMonths) && termMonths >= 1 ? termMonths : 1;
  return roundToCents(roundToCents(monthlyFee) * months);
}

function formatNumber(n: number): string {
  return new Intl.NumberFormat('en-US').format(n);
}

function formatTokens(tokens: number): string {
  if (tokens >= 1_000_000) return `${(tokens / 1_000_000).toFixed(1)}M tokens`;
  if (tokens >= 1_000) return `${(tokens / 1_000).toFixed(1)}K tokens`;
  return `${tokens} tokens`;
}

function getStatusBadgeColor(status: string): 'success' | 'danger' | 'informative' | 'brand' | 'subtle' {
  switch (status) {
    case 'Paid': return 'success';
    case 'Failed': return 'danger';
    case 'Voided': return 'subtle';
    case 'Submitted': return 'brand';
    default: return 'informative';
  }
}

// Read the backend's ProblemDetails error body defensively (the axios error interceptor does
// NOT camelCase error payloads, and ProblemDetails extension keys are written verbatim), so we
// probe both casings for the code/detail.
function readApiError(err: unknown): { code?: string; detail?: string; status?: number } {
  const e = err as { response?: { status?: number; data?: Record<string, unknown> } };
  const data = (e?.response?.data ?? {}) as Record<string, unknown>;
  const code = (data['error'] ?? data['Error']) as string | undefined;
  const detail = (data['detail'] ?? data['Detail']) as string | undefined;
  return { code, detail, status: e?.response?.status };
}

// Map a deal-create failure to a specific, actionable message (R16). The backend distinguishes
// the outcomes via the ProblemDetails `error` extension code; fall back to the detail/message.
function mapDealError(err: unknown): string {
  const { code, detail } = readApiError(err);
  switch (code) {
    case 'collect_card_first':
      return 'No card on file for this org. Use "Collect Card" first, then create the deal.';
    case 'charge_failed':
      return 'The card on file could not be charged. The deal was not activated — no entitlement, subscription, or token grant was created.';
    case 'subscription_already_live':
      return 'This org already has a live subscription — cancel it before creating a new deal.';
    case 'trial_not_supported':
      return 'CRM is charged on enable — set the trial length to 0.';
    case 'enable_in_progress':
      return 'Another request is finishing this enable. Retry in a moment.';
    case 'request_idempotency_token_required':
      return 'Could not submit safely (missing idempotency token). Reopen the dialog and try again.';
    case 'crm_employee_conflict':
      return 'This org already has a conflicting CRM employee. Resolve it before creating a CRM deal.';
    case 'post_charge_contact_support':
      return 'The charge succeeded but a follow-up step failed. This has been logged — contact support before retrying.';
    case 'prepaid_trial_not_allowed':
      return 'Prepaid deals charge the full term at creation, so they can’t include a trial. Turn off "collect full term upfront" to use a trial, or set the trial length to 0.';
    case 'prepaid_requires_price':
      return 'Prepaid deals need a monthly price above $0. Set a price, or create a $0 giveaway under monthly billing instead.';
    case 'invalid_term_months':
      return 'Enter a whole number of months between 1 and 120.';
    case 'invalid_collection_mode':
      return 'Choose either monthly billing or full-term prepay.';
    default:
      return detail || (err instanceof Error ? err.message : 'Failed to create the deal.');
  }
}

// Map an immediate-cancel (cancel-now) failure to a plain-language message by HTTP status (the U5
// endpoint distinguishes outcomes by status: 400 missing reason, 403 non-staff, 404 wrong org,
// 409 already terminal). Some m365 model-validation responses carry only `errors` with a null
// detail, so keep a status-specific fallback rather than leaning on `detail`.
function mapCancelError(err: unknown): string {
  const { detail, status } = readApiError(err);
  switch (status) {
    case 409:
      return 'This deal has already ended — there is nothing to cancel.';
    case 404:
      return 'This subscription was not found for this organization. Refresh and try again.';
    case 403:
      return 'You do not have permission to cancel this deal.';
    case 400:
      return detail || 'A reason is required to cancel this deal.';
    default:
      return detail || (err instanceof Error ? err.message : 'Failed to cancel the deal.');
  }
}

function mapGenericError(err: unknown, fallback: string): string {
  const { detail } = readApiError(err);
  return detail || (err instanceof Error ? err.message : fallback);
}

export default function OrgBillingTab({
  orgId,
  cardCollectionResult,
  cardCollectionReturnPath,
}: OrgBillingTabProps) {
  const [billingData, setBillingData] = useState<OrgBillingSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cardBannerDismissed, setCardBannerDismissed] = useState(false);
  const [viewMode, setViewMode] = useState<'product' | 'user'>('product');
  const [period, setPeriod] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [invoiceStatusFilter, setInvoiceStatusFilter] = useState('All');
  const [expandedProducts, setExpandedProducts] = useState<Set<string>>(new Set());
  const [expandedEmployees, setExpandedEmployees] = useState<Set<string>>(new Set());
  const [employees, setEmployees] = useState<AdminEmployee[]>([]);
  // Bumped whenever a sales action (deal / grant / card) changes the org's paid state. The
  // PaidProductsSection re-fetches on every change of this key, so a just-created deal repaints
  // without a page reload (trace: dialog Done -> onCreated -> onChanged() -> handleBillingChanged).
  const [paidProductsRefreshKey, setPaidProductsRefreshKey] = useState(0);

  const fetchEmployees = useCallback(async () => {
    try {
      const data = await adminApi.getOrganizationEmployees(orgId);
      setEmployees(data);
    } catch (err: unknown) {
      console.error('Failed to load employees:', err);
    }
  }, [orgId]);

  const fetchBillingData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminApi.getBillingSummary(orgId, period);
      setBillingData(data);
      // Auto-expand first product/employee
      if (data.products.length > 0) {
        setExpandedProducts(new Set([data.products[0].productCode]));
      }
      if (data.employees.length > 0) {
        setExpandedEmployees(new Set([data.employees[0].employeeId]));
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to load billing data';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [orgId, period]);

  useEffect(() => {
    fetchBillingData();
    fetchEmployees();
  }, [fetchBillingData, fetchEmployees]);

  // Single refresh entry point handed to SalesActionsCard.onChanged: refresh the month summary
  // AND signal the PaidProductsSection to re-fetch, so one refresh cycle repaints both.
  const handleBillingChanged = useCallback(() => {
    setPaidProductsRefreshKey(k => k + 1);
    fetchBillingData();
  }, [fetchBillingData]);

  const navigatePeriod = (direction: -1 | 1) => {
    const [year, month] = period.split('-').map(Number);
    const d = new Date(year, month - 1 + direction, 1);
    setPeriod(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  const periodLabel = (() => {
    const [year, month] = period.split('-').map(Number);
    return new Date(year, month - 1).toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
  })();

  const toggleProduct = (code: string) => {
    setExpandedProducts(prev => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code); else next.add(code);
      return next;
    });
  };

  const toggleEmployee = (id: string) => {
    setExpandedEmployees(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
        <Spinner label="Loading billing data..." />
      </div>
    );
  }

  if (error) {
    return (
      <Card className="info-section">
        <span className="block" style={{ color: 'hsl(var(--destructive))' }}>Error: {error}</span>
        <Button variant="ghost" onClick={fetchBillingData}>Retry</Button>
      </Card>
    );
  }

  if (!billingData) return null;

  const filteredInvoices = invoiceStatusFilter === 'All'
    ? billingData.invoices
    : billingData.invoices.filter(inv => inv.status === invoiceStatusFilter);

  const subscriptionProducts = billingData.products.filter(p => p.category === 'subscription');
  const usageProducts = billingData.products.filter(p => p.category === 'usage');

  const showCardBanner = !!cardCollectionResult && !cardBannerDismissed;

  return (
    <div className="billing-tab" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {/* Card-collection return confirmation (U4). The billing summary already re-fetched on mount,
          so a saved card is reflected in the Stripe row / Sales Actions hint below. */}
      {showCardBanner && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '0.75rem',
            padding: '0.6rem 0.85rem',
            borderRadius: '6px',
            border: `1px solid ${cardCollectionResult === 'collected' ? 'hsl(var(--success) / 0.3)' : 'hsl(var(--warning) / 0.3)'}`,
            background: cardCollectionResult === 'collected' ? 'hsl(var(--success) / 0.1)' : 'hsl(var(--warning) / 0.1)',
            color: cardCollectionResult === 'collected' ? 'hsl(var(--success))' : 'hsl(var(--warning))',
            fontSize: '0.88rem',
          }}
        >
          <span>
            {cardCollectionResult === 'collected'
              ? 'Card saved — a card is now on file for this organization.'
              : 'Card collection was cancelled — no card was saved.'}
          </span>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => setCardBannerDismissed(true)}
            title="Dismiss"
          >
            <X />
          </Button>
        </div>
      )}

      {/* Period Selector + View Toggle */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigatePeriod(-1)} title="Previous month">
            <ChevronLeft />
          </Button>
          <span style={{ fontWeight: 600, minWidth: '140px', textAlign: 'center' }}>{periodLabel}</span>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigatePeriod(1)} title="Next month">
            <ChevronRight />
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={fetchBillingData} title="Refresh">
            <RotateCw />
          </Button>
        </div>
        <div style={{ display: 'flex', gap: '2px', background: 'hsl(var(--secondary))', borderRadius: '6px', padding: '2px' }}>
          <button
            onClick={() => setViewMode('product')}
            style={{
              padding: '0.35rem 0.75rem',
              border: 'none',
              borderRadius: '4px',
              background: viewMode === 'product' ? 'hsl(var(--card))' : 'transparent',
              boxShadow: viewMode === 'product' ? '0 1px 3px rgba(0,0,0,0.12)' : 'none',
              fontWeight: viewMode === 'product' ? 600 : 400,
              fontSize: '0.85rem',
              cursor: 'pointer',
            }}
          >
            By Product
          </button>
          <button
            onClick={() => setViewMode('user')}
            style={{
              padding: '0.35rem 0.75rem',
              border: 'none',
              borderRadius: '4px',
              background: viewMode === 'user' ? 'hsl(var(--card))' : 'transparent',
              boxShadow: viewMode === 'user' ? '0 1px 3px rgba(0,0,0,0.12)' : 'none',
              fontWeight: viewMode === 'user' ? 600 : 400,
              fontSize: '0.85rem',
              cursor: 'pointer',
            }}
          >
            By User
          </button>
        </div>
      </div>

      {/* Summary Card */}
      <BillingSummaryCard data={billingData} />

      {/* Sales-operated actions: custom deal, card collection, bonus token grants + audit (U8–U10) */}
      <SalesActionsCard
        orgId={orgId}
        hasCardOnFile={!!billingData.stripeCustomerId}
        cardCollectionReturnPath={cardCollectionReturnPath ?? getOrgDetailUrl(orgId)}
        onChanged={handleBillingChanged}
      />

      {/* Read-only paid-product subscriptions (all lifecycle states). Refetches on its own mount
          and whenever a sales action bumps paidProductsRefreshKey, so a new deal appears without
          a reload. Owns its loading/error state — a failure here never blanks the rest of the tab. */}
      <PaidProductsSection orgId={orgId} refreshKey={paidProductsRefreshKey} />

      {/* By Product View */}
      {viewMode === 'product' && (
        <>
          <SubscriptionsSection
            products={subscriptionProducts}
            expanded={expandedProducts}
            onToggle={toggleProduct}
            employees={employees}
            orgId={orgId}
            onEmployeeUpdated={() => { fetchEmployees(); fetchBillingData(); }}
            ratePlanName={billingData.ratePlanName}
          />
          <UsageSection
            products={usageProducts}
            expanded={expandedProducts}
            onToggle={toggleProduct}
          />
        </>
      )}

      {/* By User View */}
      {viewMode === 'user' && (
        <ByUserSection
          employees={billingData.employees}
          expanded={expandedEmployees}
          onToggle={toggleEmployee}
          usageTotal={billingData.usageTotal}
        />
      )}

      {/* Invoices (shared) */}
      <InvoicesSection
        invoices={filteredInvoices}
        allInvoices={billingData.invoices}
        statusFilter={invoiceStatusFilter}
        onStatusFilterChange={setInvoiceStatusFilter}
        orgId={orgId}
        hasStripe={!!billingData.stripeCustomerId}
        onInvoiceCreated={fetchBillingData}
      />
    </div>
  );
}

// --- Sub-components ---

function BillingSummaryCard({ data }: { data: OrgBillingSummary }) {
  return (
    <Card className="info-section">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <h3>Month Summary</h3>
        <span style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.85rem' }}>{data.billingPeriod}</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '1rem', margin: '0.75rem 0' }}>
        <div>
          <div style={{ fontSize: '0.8rem', color: 'hsl(var(--muted-foreground))' }}>Total</div>
          <div style={{ fontSize: '1.25rem', fontWeight: 700 }}>{formatCurrency(data.totalAmount)}</div>
        </div>
        <div>
          <div style={{ fontSize: '0.8rem', color: 'hsl(var(--muted-foreground))' }}>Subscriptions</div>
          <div style={{ fontSize: '1.1rem', fontWeight: 600 }}>{formatCurrency(data.subscriptionTotal)}</div>
        </div>
        <div>
          <div style={{ fontSize: '0.8rem', color: 'hsl(var(--muted-foreground))' }}>Usage</div>
          <div style={{ fontSize: '1.1rem', fontWeight: 600 }}>{formatCurrency(data.usageTotal)}</div>
        </div>
        <div>
          <div style={{ fontSize: '0.8rem', color: 'hsl(var(--muted-foreground))' }}>Credits</div>
          <div style={{ fontSize: '1.1rem', fontWeight: 600 }}>-{formatCurrency(data.credits)}</div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', fontSize: '0.85rem', color: 'hsl(var(--foreground))', borderTop: '1px solid hsl(var(--border))', paddingTop: '0.5rem' }}>
        <span>
          Stripe:{' '}
          {data.stripeCustomerId ? (
            <>
              <CopyableId value={data.stripeCustomerId} />
              <a
                href={`https://dashboard.stripe.com/customers/${data.stripeCustomerId}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ marginLeft: '0.25rem' }}
              >
                <ExternalLink style={{ width: 14, height: 14, verticalAlign: 'middle' }} />
              </a>
            </>
          ) : (
            <span style={{ color: 'hsl(var(--destructive))' }}>Not linked</span>
          )}
        </span>
        {data.ratePlanName && (
          <span>Rate Plan: <strong>{data.ratePlanName}</strong></span>
        )}
        {data.budgetLimit > 0 && (
          <span>Budget: {formatCurrency(data.budgetLimit)}</span>
        )}
      </div>
    </Card>
  );
}

// Map a paid-product subscription's lifecycle status to a Badge color + human label.
// TOLERANT by contract: an unrecognized status must never crash or blank — it falls through to a
// neutral 'informative' chip showing the raw status string.
// Derive a subscription's term-display facts from the additive term fields. A pre-feature row (all
// term fields null) and every default one-month deal (termMonths null/≤1) come back with hasTerm
// false → the row renders exactly as before this feature: no term text, no badge, no changed
// wording. `ended` compares against the stamped committedThroughDate (display-only; the billing
// engine uses month-index math, but the stamped date is the honest display end).
export function getTermDisplay(sub: AdminProductSubscription): {
  hasTerm: boolean;
  isPrepaid: boolean;
  inTermPrepaid: boolean;
  showPrepaidBadge: boolean;
  ended: boolean;
  termText: string | null;
} {
  // Cancelled / Revoked are terminal: the deal is fully over, so it never shows the "rolled to
  // month-to-month" framing (that is for a LIVE deal whose term elapsed) — it shows its term facts
  // factually so a rep can read and recreate it.
  const isTerminal = sub.status === 'Cancelled' || sub.status === 'Revoked';
  const termMonths = sub.termMonths ?? 0;
  const hasTerm = termMonths > 1 && !!sub.committedThroughDate;
  // Case-insensitive: the wire value is lowercase "prepaid", but compare defensively so a casing
  // change on the backend can never silently drop the prepaid treatment.
  const isPrepaid = sub.collectionMode?.toLowerCase() === 'prepaid';
  const pastThrough = hasTerm && !!sub.committedThroughDate && new Date(sub.committedThroughDate).getTime() < Date.now();
  // A LIVE deal whose committed term has elapsed — it now bills month-to-month.
  const ended = hasTerm && pastThrough && !isTerminal;
  // In-term prepaid (live or pending-cancel) charges nothing on its upcoming date (tokens only), so
  // its upcoming date reads "tokens renew"; once a live deal's term ends it rolls to month-to-month
  // and bills again ("next billing").
  const inTermPrepaid = isPrepaid && hasTerm && !pastThrough && !isTerminal;

  let termText: string | null = null;
  if (hasTerm) {
    termText = ended
      ? 'term ended · month-to-month'
      : `${termMonths}-mo term through ${formatDate(sub.committedThroughDate!)}`;
  }
  // The Prepaid tag reflects collection mode alone and shows on every prepaid row — live, pending, or
  // terminal (a rep reading a cancelled/revoked deal still sees it was collected upfront), and
  // regardless of term length: a paid-upfront 1-month deal (no term chrome) still carries the badge.
  const showPrepaidBadge = isPrepaid;

  return { hasTerm, isPrepaid, inTermPrepaid, showPrepaidBadge, ended, termText };
}

// Live rows (in trial or active) offer Cancel now; a prepaid deal already scheduled to cancel at
// term end (CancelledAtPeriodEnd) still grants monthly tokens, so it offers it too. Already-ended
// rows (Cancelled / Revoked) do not.
function canCancelNow(sub: AdminProductSubscription): boolean {
  return sub.status === 'Active'
    || sub.status === 'Trialing'
    || sub.status === 'CancelledAtPeriodEnd';
}

function getSubscriptionPill(sub: AdminProductSubscription): {
  color: 'success' | 'warning' | 'informative' | 'danger';
  text: string;
} {
  switch (sub.status) {
    case 'Trialing':
      return {
        color: 'warning',
        text: sub.trialEndsAt ? `Trialing · ends ${formatDate(sub.trialEndsAt)}` : 'Trialing',
      };
    case 'Active':
      return {
        color: 'success',
        text: sub.nextBillingDate ? `Active · next charge ${formatDate(sub.nextBillingDate)}` : 'Active',
      };
    case 'CancelledAtPeriodEnd': {
      const endDate = sub.nextBillingDate ?? sub.cancelledAt;
      return {
        color: 'informative',
        text: endDate ? `Ending ${formatDate(endDate)}` : 'Ending at period end',
      };
    }
    case 'Cancelled':
      return { color: 'informative', text: 'Cancelled' };
    case 'Revoked':
      return { color: 'danger', text: 'Revoked' };
    default:
      // Unknown/unexpected status — neutral chip, never blank.
      return { color: 'informative', text: sub.status };
  }
}

// Read-only paid-product subscriptions (all lifecycle states) with a staff-only Cancel now control
// on live/prepaid-pending rows. Owns its own fetch/loading/error so a failure here never blanks the
// rest of the Billing tab. Re-fetches on mount, on every `refreshKey` bump (a just-created deal
// repaints without a reload), and on its own `localRefresh` bump after an immediate cancel.
function PaidProductsSection({ orgId, refreshKey }: { orgId: string; refreshKey: number }) {
  const [subscriptions, setSubscriptions] = useState<AdminProductSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Bumped after a successful immediate-cancel to repaint the row (background refetch, no spinner).
  const [localRefresh, setLocalRefresh] = useState(0);
  // The subscription whose Cancel now confirmation dialog is open (null = closed).
  const [cancelTarget, setCancelTarget] = useState<AdminProductSubscription | null>(null);
  // Tracks the org whose list is already loaded, so we can tell a first load apart from a background
  // refetch. A `refreshKey` bump after a just-created deal fires a background refetch: on a transient
  // GET failure we keep the existing list rendered instead of blanking it with a red error (which
  // would falsely imply the just-created deal failed). It self-recovers on the next refresh.
  const loadedForOrgRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const isInitialLoad = loadedForOrgRef.current !== orgId;
    // Only the initial load (no list yet for this org) shows the loading-only view and clears the
    // error up front; a background refetch keeps the current list on screen while it revalidates.
    if (isInitialLoad) {
      setLoading(true);
      setError(null);
    }
    (async () => {
      try {
        const data = await adminApi.getProductSubscriptions(orgId);
        if (!cancelled) {
          setSubscriptions(data);
          setError(null);
          loadedForOrgRef.current = orgId;
        }
      } catch (err: unknown) {
        // Surface the inline error only on the initial load. On a background refetch keep the prior
        // list rendered rather than replacing it with the error.
        if (!cancelled && isInitialLoad) setError(mapGenericError(err, 'Failed to load paid products'));
      } finally {
        if (!cancelled && isInitialLoad) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [orgId, refreshKey, localRefresh]);

  return (
    <Card className="info-section">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3>Paid Products</h3>
      </div>
      {loading ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.75rem' }}>
          <Spinner size={16} />
          <span style={{ color: 'hsl(var(--muted-foreground))' }}>Loading paid products…</span>
        </div>
      ) : error ? (
        <div style={{ color: 'hsl(var(--destructive))', fontSize: '0.85rem', marginTop: '0.75rem' }}>{error}</div>
      ) : subscriptions.length === 0 ? (
        <span className="block" style={{ color: 'hsl(var(--muted-foreground))', marginTop: '0.75rem' }}>No paid-product subscriptions</span>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.75rem' }}>
          {subscriptions.map(sub => {
            const pill = getSubscriptionPill(sub);
            const term = getTermDisplay(sub);
            // In-term prepaid rows charge nothing on the upcoming date (tokens only); every other
            // row (monthly, or a prepaid deal rolled to month-to-month) keeps "next billing".
            const dateWord = term.inTermPrepaid ? 'tokens renew' : 'next billing';
            return (
              <div
                key={sub.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '0.5rem',
                  padding: '0.5rem 0',
                  borderBottom: '1px solid hsl(var(--border))',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 600 }}>{getProductName(sub.productId)}</span>
                  <Badge variant="outline" className={badgeClass(pill.color)}>{pill.text}</Badge>
                  {term.showPrepaidBadge && (
                    <Badge variant="outline" className={badgeClass('brand')}>Prepaid</Badge>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                  <div style={{ color: 'hsl(var(--foreground))', fontSize: '0.9rem' }}>
                    ${sub.monthlyPriceUsd}/mo · {formatTokens(sub.monthlyTokenAllotment)}/mo
                    {term.termText && (
                      <span style={{ color: 'hsl(var(--muted-foreground))' }}> · {term.termText}</span>
                    )}
                    {sub.nextBillingDate && (
                      <span style={{ color: 'hsl(var(--muted-foreground))' }}> · {dateWord} {formatDate(sub.nextBillingDate)}</span>
                    )}
                  </div>
                  {canCancelNow(sub) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setCancelTarget(sub)}
                    >
                      Cancel now
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {cancelTarget && (
        <CancelNowDialog
          orgId={orgId}
          subscription={cancelTarget}
          onCancelled={() => { setCancelTarget(null); setLocalRefresh(k => k + 1); }}
          onClose={() => setCancelTarget(null)}
        />
      )}
    </Card>
  );
}

// Immediate-cancel confirmation (plan U5/U7). Requires a typed reason (confirm blocked while empty,
// and the backend refuses an empty reason too). Copy states access + billing end at once, no refund,
// and a new deal can follow — with the prepaid-trial caveat. For a prepaid mid-term row it adds the
// forfeit warning naming the real paid-through date.
export function CancelNowDialog({
  orgId,
  subscription,
  onCancelled,
  onClose,
}: {
  orgId: string;
  subscription: AdminProductSubscription;
  onCancelled: () => void;
  onClose: () => void;
}) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const term = getTermDisplay(subscription);
  const canConfirm = !submitting && reason.trim() !== '';

  const handleConfirm = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await adminApi.cancelProductSubscriptionNow(orgId, subscription.id, reason.trim());
      onCancelled();
    } catch (err: unknown) {
      setError(mapCancelError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    // While the cancel POST is in flight, block backdrop dismissal: dismissing would read as "kept"
    // to the rep while the cancel still lands on the server. (The Modal has no built-in Escape
    // handling, so backdrop + the disabled "Keep deal" button are the only dismissal paths to gate.)
    <Modal open onOverlayClick={submitting ? undefined : onClose}>
      <Modal.Content maxWidth="lg" aria-labelledby="cancel-now-title">
        <Modal.Header className="px-6 py-4">
          <h2 id="cancel-now-title" className="text-lg font-semibold text-foreground">
            Cancel {getProductName(subscription.productId)} now?
          </h2>
        </Modal.Header>
        <Modal.Body className="px-6 py-4">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
            <div style={{ fontSize: '0.9rem', color: 'hsl(var(--foreground))' }}>
              Immediately ends access and billing for this product. No refund is issued. You can create
              a new deal right after — but a prepaid replacement deal can’t use a trial to cover days
              already paid for, so either price the overlap into the new deal or collect the replacement
              monthly instead.
            </div>

            {term.inTermPrepaid && subscription.committedThroughDate && (
              <div style={{ background: 'hsl(var(--destructive) / 0.1)', border: '1px solid hsl(var(--destructive) / 0.3)', borderRadius: '6px', padding: '0.6rem 0.75rem', fontSize: '0.85rem', color: 'hsl(var(--destructive))' }}>
                This org has prepaid through {formatDate(subscription.committedThroughDate)} — cancelling
                now forfeits the remaining prepaid months. No refund is issued.
              </div>
            )}

            <div>
              <label style={{ fontSize: '0.85rem', fontWeight: 600 }}>Reason</label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Why is this deal being cancelled now?"
                rows={2}
              />
            </div>

            {error && (
              <div style={{ color: 'hsl(var(--destructive))', fontSize: '0.85rem' }}>{error}</div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer className="flex justify-end gap-2 px-6 py-4">
          <Button variant="secondary" onClick={onClose} disabled={submitting}>Keep deal</Button>
          <Button variant="destructive" onClick={handleConfirm} disabled={!canConfirm}>
            {submitting ? 'Cancelling…' : 'Cancel now'}
          </Button>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
}

const TEMPLATE_CATEGORIES: Record<string, string> = {
  'generic-chat': 'General',
  'casey-support': 'Support',
  'charter-quoting': 'Sales',
  'quinn-sales': 'Sales',
  'sage-sales-trainer': 'Sales',
  'coach-sales-coaching': 'Sales',
  'devin-developer': 'Technology',
  'alex-pm': 'Operations',
  'sam-ea': 'Executive',
  'blake-legal': 'Legal',
  'harper-hr': 'HR',
  'jordan-analyst': 'Analytics',
  'marshall-pm': 'Operations',
  'morgan-finance': 'Finance',
  'riley-social': 'Marketing',
  'taylor-marketing': 'Marketing',
  'moira-compliance': 'Legal',
  'lana-admin': 'Operations',
  'flyex-ops': 'Support',
};

const SUBSCRIPTION_STATUSES = ['active', 'canceled', 'past_due', 'trialing', 'unpaid'];

function getSubStatusColor(status: string): 'success' | 'danger' | 'warning' | 'informative' | 'subtle' {
  switch (status) {
    case 'active': return 'success';
    case 'trialing': return 'informative';
    case 'canceled': return 'danger';
    case 'past_due':
    case 'unpaid': return 'warning';
    default: return 'subtle';
  }
}

function getEmpStatusColor(status: string): 'success' | 'danger' | 'informative' | 'subtle' {
  switch (status) {
    case 'Active': return 'success';
    case 'Suspended': return 'danger';
    case 'Deleted': return 'subtle';
    default: return 'informative';
  }
}

function getTemplateCategory(sourceTemplateId?: string): string {
  if (!sourceTemplateId) return 'General';
  return TEMPLATE_CATEGORIES[sourceTemplateId] ?? 'General';
}

function SubscriptionsSection({
  products,
  expanded,
  onToggle,
  employees,
  orgId,
  onEmployeeUpdated,
  ratePlanName,
}: {
  products: ProductBillingEntry[];
  expanded: Set<string>;
  onToggle: (code: string) => void;
  employees: AdminEmployee[];
  orgId: string;
  onEmployeeUpdated: () => void;
  ratePlanName: string | null;
}) {
  const total = products.reduce((sum, p) => sum + p.totalAmount, 0);
  return (
    <Card className="info-section">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h3>Subscriptions</h3>
          {ratePlanName && (
            <div style={{ fontSize: '0.8rem', color: 'hsl(var(--muted-foreground))', marginTop: '2px' }}>{ratePlanName}</div>
          )}
        </div>
        <span style={{ fontWeight: 600, color: 'hsl(var(--foreground))' }}>{formatCurrency(total)}/mo</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.75rem' }}>
        {products.map(product => (
          <ProductGroup
            key={product.productCode}
            product={product}
            isExpanded={expanded.has(product.productCode)}
            onToggle={() => onToggle(product.productCode)}
            employees={product.productCode === 'aie' ? employees : undefined}
            orgId={orgId}
            onEmployeeUpdated={onEmployeeUpdated}
            ratePlanName={ratePlanName}
          />
        ))}
        {products.length === 0 && (
          <span className="block" style={{ color: 'hsl(var(--muted-foreground))' }}>No active subscriptions</span>
        )}
      </div>
    </Card>
  );
}

function ProductGroup({
  product,
  isExpanded,
  onToggle,
  employees,
  orgId,
  onEmployeeUpdated,
  ratePlanName,
}: {
  product: ProductBillingEntry;
  isExpanded: boolean;
  onToggle: () => void;
  employees?: AdminEmployee[];
  orgId: string;
  onEmployeeUpdated: () => void;
  ratePlanName: string | null;
}) {
  const [expandedEmployeeIds, setExpandedEmployeeIds] = useState<Set<string>>(new Set());
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<UpdateEmployeeSubscriptionRequest>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const toggleEmployeeRow = (id: string) => {
    setExpandedEmployeeIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const startEdit = (emp: AdminEmployee) => {
    setEditingId(emp.id);
    setSaveError(null);
    setEditForm({
      subscriptionId: emp.subscription?.subscriptionId ?? '',
      priceId: emp.subscription?.priceId ?? '',
      status: emp.subscription?.status ?? '',
    });
    if (!expandedEmployeeIds.has(emp.id)) {
      setExpandedEmployeeIds(prev => new Set(prev).add(emp.id));
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
    setSaveError(null);
  };

  const handleSave = async (employeeId: string) => {
    setSaving(true);
    setSaveError(null);
    try {
      await adminApi.updateEmployeeSubscription(orgId, employeeId, editForm);
      setEditingId(null);
      setEditForm({});
      onEmployeeUpdated();
    } catch (err: unknown) {
      // Keep the form open on failure so the rep can retry; surface the error inline.
      const msg = err instanceof Error ? err.message : 'Failed to update subscription';
      setSaveError(msg);
    } finally {
      setSaving(false);
    }
  };

  const isAie = product.productCode === 'aie' && employees;
  const hasBreakdown = product.employeeBreakdown.length > 0;

  return (
    <div style={{ border: '1px solid hsl(var(--border))', borderRadius: '6px', overflow: 'hidden' }}>
      <button
        onClick={onToggle}
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '0.6rem 0.75rem',
          border: 'none',
          background: isExpanded ? 'hsl(var(--secondary))' : 'hsl(var(--card))',
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <span style={{ fontWeight: 600 }}>
          {isExpanded ? '▾' : '▸'} {product.productName}
        </span>
        <span style={{ color: 'hsl(var(--foreground))', fontSize: '0.9rem' }}>
          {product.quantity != null && product.unitPrice != null
            ? `${product.quantity} seat${product.quantity !== 1 ? 's' : ''} × ${formatCurrency(product.unitPrice)} = ${formatCurrency(product.totalAmount)}`
            : formatCurrency(product.totalAmount)}
        </span>
      </button>
      {isExpanded && isAie && (
        <div style={{ padding: '0 0.75rem 0.75rem' }}>
          {employees.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '0.5rem' }}>
              {employees.map(emp => {
                const isRowExpanded = expandedEmployeeIds.has(emp.id);
                const isEditing = editingId === emp.id;
                const category = getTemplateCategory(emp.sourceTemplateId);
                const displayName = emp.preferredName ?? emp.name;
                const breakdown = product.employeeBreakdown.find(b => b.employeeId === emp.id);
                const cost = breakdown?.cost;
                const tokens = breakdown?.consumption ?? 0;
                const isNonSubscribed = !emp.subscription || emp.subscription.status === 'canceled';

                return (
                  <div key={emp.id} style={{ border: '1px solid hsl(var(--border))', borderRadius: '4px', overflow: 'hidden', opacity: isNonSubscribed ? 0.7 : 1 }}>
                    <button
                      onClick={() => toggleEmployeeRow(emp.id)}
                      style={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '0.4rem 0.6rem',
                        border: 'none',
                        background: isRowExpanded ? 'hsl(var(--secondary))' : 'hsl(var(--card))',
                        cursor: 'pointer',
                        textAlign: 'left',
                        gap: '0.5rem',
                      }}
                    >
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1, minWidth: 0 }}>
                        <span style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>{isRowExpanded ? '▾' : '▸'}</span>
                        <span style={{ fontWeight: isNonSubscribed ? 400 : 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: isNonSubscribed ? 'hsl(var(--muted-foreground))' : undefined }}>
                          {displayName}
                        </span>
                        <Badge variant="outline" className={badgeClass('brand')} style={{ flexShrink: 0 }}>
                          {category} · {emp.typeId}
                        </Badge>
                        <Badge variant="outline" className={badgeClass(getEmpStatusColor(emp.employeeStatus))} style={{ flexShrink: 0 }}>
                          {emp.employeeStatus}
                        </Badge>
                        {emp.subscription ? (
                          <Badge variant="outline" className={badgeClass(getSubStatusColor(emp.subscription.status))} style={{ flexShrink: 0 }}>
                            {emp.subscription.status}
                          </Badge>
                        ) : (
                          <span style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.8rem', flexShrink: 0 }}>No subscription</span>
                        )}
                      </span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexShrink: 0 }}>
                        <span style={{ fontSize: '0.8rem', color: 'hsl(var(--muted-foreground))' }}>{formatTokens(tokens)}</span>
                        {!isNonSubscribed && cost != null ? (
                          <span style={{ fontWeight: 600, fontSize: '0.9rem', color: 'hsl(var(--foreground))' }}>{formatCurrency(cost)}/mo</span>
                        ) : (
                          <span style={{ fontSize: '0.85rem', color: 'hsl(var(--muted-foreground))' }}>-</span>
                        )}
                      </span>
                    </button>

                    {isRowExpanded && !isEditing && (
                      <div style={{ padding: '0.5rem 0.6rem 0.5rem', paddingLeft: '2rem', background: 'hsl(var(--secondary))', fontSize: '0.85rem' }}>
                        {emp.subscription && !isNonSubscribed ? (
                          <>
                            <div style={{ fontWeight: 600, marginBottom: '0.3rem' }}>
                              Billing: {formatDate(emp.subscription.currentPeriodStart)} – {formatDate(emp.subscription.currentPeriodEnd)}
                              {ratePlanName && cost != null && (
                                <span style={{ fontWeight: 400, color: 'hsl(var(--muted-foreground))' }}> · Rate Plan: {ratePlanName} ({formatCurrency(cost)}/mo)</span>
                              )}
                            </div>
                            <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center', flexWrap: 'wrap', color: 'hsl(var(--muted-foreground))' }}>
                              <span>Sub ID: {emp.subscription.subscriptionId ? <CopyableId value={emp.subscription.subscriptionId} /> : '-'}</span>
                              <span>Price ID: {emp.subscription.priceId ? <code style={{ fontSize: '0.8rem' }}>{emp.subscription.priceId}</code> : '-'}</span>
                              <span>Tokens: {formatNumber(tokens)} this period</span>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => { e.stopPropagation(); startEdit(emp); }}
                              >
                                <Pencil />
                                Edit Subscription
                              </Button>
                            </div>
                          </>
                        ) : (
                          <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center', flexWrap: 'wrap', color: 'hsl(var(--muted-foreground))' }}>
                            <span>No active subscription</span>
                            <span>{formatNumber(tokens)} tokens this period</span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); startEdit(emp); }}
                            >
                              <Pencil />
                              Edit Subscription
                            </Button>
                          </div>
                        )}
                      </div>
                    )}

                    {isRowExpanded && isEditing && (
                      <div style={{ padding: '0.5rem 0.6rem', paddingLeft: '2rem', background: 'hsl(var(--secondary))', display: 'flex', gap: '0.75rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                        <div>
                          <label style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>Status</label>
                          <Select
                            value={editForm.status ? editForm.status : 'none'}
                            onValueChange={(v) => setEditForm(prev => ({ ...prev, status: v === 'none' ? '' : v }))}
                          >
                            <SelectTrigger className="h-8 min-w-[110px] text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">(none)</SelectItem>
                              {SUBSCRIPTION_STATUSES.map(s => (
                                <SelectItem key={s} value={s}>{s}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div style={{ flex: 1, minWidth: '150px' }}>
                          <label style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>Subscription ID</label>
                          <Input
                            value={editForm.subscriptionId ?? ''}
                            onChange={(e) => setEditForm(prev => ({ ...prev, subscriptionId: e.target.value }))}
                            placeholder="sub_xxx"
                            className="h-8"
                          />
                        </div>
                        <div style={{ flex: 1, minWidth: '150px' }}>
                          <label style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>Price ID</label>
                          <Input
                            value={editForm.priceId ?? ''}
                            onChange={(e) => setEditForm(prev => ({ ...prev, priceId: e.target.value }))}
                            placeholder="price_xxx"
                            className="h-8"
                          />
                        </div>
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <Button
                            size="sm"
                            onClick={() => handleSave(emp.id)}
                            disabled={saving}
                          >
                            {saving ? '...' : 'Save'}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={cancelEdit}
                            disabled={saving}
                          >
                            Cancel
                          </Button>
                        </div>
                        {saveError && (
                          <div style={{ flexBasis: '100%', color: 'hsl(var(--destructive))', fontSize: '0.85rem' }}>{saveError}</div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <span className="block" style={{ color: 'hsl(var(--muted-foreground))', marginTop: '0.5rem', fontSize: '0.85rem' }}>
              No employees found
            </span>
          )}
          {product.marketplaceSubscriptionId && (
            <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: 'hsl(var(--muted-foreground))', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Badge variant="outline" className={badgeClass('informative')}>Mktpl</Badge>
              <CopyableId value={product.marketplaceSubscriptionId} />
              {product.marketplacePlanId && (
                <span>· Plan: <code>{product.marketplacePlanId}</code></span>
              )}
            </div>
          )}
        </div>
      )}
      {isExpanded && !isAie && hasBreakdown && (
        <div style={{ padding: '0 0.75rem 0.75rem' }}>
          <table className="admin-table" style={{ marginTop: '0.5rem' }}>
            <thead>
              <tr>
                <th style={{ width: '40%' }}>Employee</th>
                <th style={{ width: '20%' }}>Status</th>
                <th style={{ width: '20%' }}>Since</th>
                <th style={{ width: '20%', textAlign: 'right' }}>Cost</th>
              </tr>
            </thead>
            <tbody>
              {product.employeeBreakdown.map(emp => (
                <tr key={emp.employeeId}>
                  <td>{emp.employeeName}</td>
                  <td>
                    <Badge
                      variant="outline"
                      className={badgeClass(emp.status === 'active' ? 'success' : emp.status === 'canceled' ? 'danger' : 'informative')}
                    >
                      {emp.status || 'active'}
                    </Badge>
                  </td>
                  <td style={{ color: 'hsl(var(--muted-foreground))' }}>-</td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatCurrency(emp.cost)}/mo</td>
                </tr>
              ))}
            </tbody>
          </table>
          {product.marketplaceSubscriptionId && (
            <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: 'hsl(var(--muted-foreground))', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Badge variant="outline" className={badgeClass('informative')}>Mktpl</Badge>
              <CopyableId value={product.marketplaceSubscriptionId} />
              {product.marketplacePlanId && (
                <span>· Plan: <code>{product.marketplacePlanId}</code></span>
              )}
            </div>
          )}
        </div>
      )}
      {isExpanded && !isAie && !hasBreakdown && (
        <div style={{ padding: '0.5rem 0.75rem', color: 'hsl(var(--muted-foreground))', fontSize: '0.85rem' }}>
          No employees assigned to this product
        </div>
      )}
    </div>
  );
}

function UsageSection({
  products,
  expanded,
  onToggle,
}: {
  products: ProductBillingEntry[];
  expanded: Set<string>;
  onToggle: (code: string) => void;
}) {
  const total = products.reduce((sum, p) => sum + p.totalAmount, 0);
  return (
    <Card className="info-section">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3>Usage</h3>
        <span style={{ fontWeight: 600, color: 'hsl(var(--foreground))' }}>{formatCurrency(total)}</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.75rem' }}>
        {products.map(product => (
          <UsageGroup
            key={product.productCode}
            product={product}
            isExpanded={expanded.has(product.productCode)}
            onToggle={() => onToggle(product.productCode)}
          />
        ))}
        {products.length === 0 && (
          <span className="block" style={{ color: 'hsl(var(--muted-foreground))' }}>No usage recorded for this period</span>
        )}
      </div>
    </Card>
  );
}

function UsageGroup({
  product,
  isExpanded,
  onToggle,
}: {
  product: ProductBillingEntry;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  return (
    <div style={{ border: '1px solid hsl(var(--border))', borderRadius: '6px', overflow: 'hidden' }}>
      <button
        onClick={onToggle}
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '0.6rem 0.75rem',
          border: 'none',
          background: isExpanded ? 'hsl(var(--secondary))' : 'hsl(var(--card))',
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <span style={{ fontWeight: 600 }}>
          {isExpanded ? '▾' : '▸'} {product.productName}
        </span>
        <span style={{ color: 'hsl(var(--foreground))', fontSize: '0.9rem' }}>
          {formatCurrency(product.totalAmount)}
        </span>
      </button>
      {isExpanded && (
        <div style={{ padding: '0.5rem 0.75rem 0.75rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.75rem', marginBottom: '0.5rem' }}>
            {product.includedAmount != null && (
              <div>
                <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>Pool</div>
                <div style={{ fontWeight: 600 }}>{formatNumber(product.includedAmount)}</div>
              </div>
            )}
            {product.totalConsumption != null && (
              <div>
                <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>Used</div>
                <div style={{ fontWeight: 600 }}>{formatNumber(product.totalConsumption)}</div>
              </div>
            )}
            {product.overageAmount != null && (
              <div>
                <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>Overage</div>
                <div style={{ fontWeight: 600, color: product.overageAmount > 0 ? 'hsl(var(--destructive))' : 'inherit' }}>
                  {formatNumber(product.overageAmount)}
                </div>
              </div>
            )}
            {product.unitRate != null && product.unitLabel && (
              <div>
                <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>Rate</div>
                <div style={{ fontWeight: 600 }}>{formatCurrency(product.unitRate)} {product.unitLabel}</div>
              </div>
            )}
          </div>
          {product.employeeBreakdown.length > 0 ? (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th style={{ textAlign: 'right' }}>Consumption</th>
                  <th style={{ textAlign: 'right' }}>% of Pool</th>
                  <th style={{ textAlign: 'right' }}>Cost</th>
                </tr>
              </thead>
              <tbody>
                {product.employeeBreakdown.map(emp => (
                  <tr key={emp.employeeId}>
                    <td>{emp.employeeName}</td>
                    <td style={{ textAlign: 'right' }}>{emp.consumption != null ? formatNumber(emp.consumption) : '-'}</td>
                    <td style={{ textAlign: 'right' }}>{emp.percentOfTotal != null ? `${emp.percentOfTotal.toFixed(1)}%` : '-'}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatCurrency(emp.cost)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <span className="block" style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.85rem' }}>
              Org-level totals only — per-employee breakdown coming soon
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function ByUserSection({
  employees,
  expanded,
  onToggle,
  usageTotal,
}: {
  employees: EmployeeBillingEntry[];
  expanded: Set<string>;
  onToggle: (id: string) => void;
  usageTotal: number;
}) {
  return (
    <>
      <Card className="info-section">
        <h3>Cost By User</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.75rem' }}>
          {employees.map(emp => (
            <EmployeeGroup
              key={emp.employeeId}
              employee={emp}
              isExpanded={expanded.has(emp.employeeId)}
              onToggle={() => onToggle(emp.employeeId)}
            />
          ))}
          {employees.length === 0 && (
            <span className="block" style={{ color: 'hsl(var(--muted-foreground))' }}>No employees found</span>
          )}
        </div>
      </Card>
      {usageTotal > 0 && (
        <Card className="info-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3>Unassigned Costs</h3>
            <span style={{ fontWeight: 600, color: 'hsl(var(--foreground))' }}>{formatCurrency(usageTotal)}</span>
          </div>
          <span className="block" style={{ color: 'hsl(var(--muted-foreground))', marginTop: '0.5rem' }}>
            Token pool overage (shared, not attributable to individual users): {formatCurrency(usageTotal)}
          </span>
        </Card>
      )}
    </>
  );
}

function EmployeeGroup({
  employee,
  isExpanded,
  onToggle,
}: {
  employee: EmployeeBillingEntry;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  return (
    <div style={{ border: '1px solid hsl(var(--border))', borderRadius: '6px', overflow: 'hidden' }}>
      <button
        onClick={onToggle}
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '0.6rem 0.75rem',
          border: 'none',
          background: isExpanded ? 'hsl(var(--secondary))' : 'hsl(var(--card))',
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <span style={{ fontWeight: 600 }}>
          {isExpanded ? '▾' : '▸'} {employee.employeeName}
        </span>
        <span style={{ color: 'hsl(var(--foreground))', fontSize: '0.9rem' }}>
          Total: {formatCurrency(employee.totalCost)}
        </span>
      </button>
      {isExpanded && (
        <div style={{ padding: '0 0.75rem 0.75rem' }}>
          <table className="admin-table" style={{ marginTop: '0.5rem' }}>
            <thead>
              <tr>
                <th style={{ width: '30%' }}>Product</th>
                <th style={{ width: '20%' }}>Type</th>
                <th style={{ width: '30%' }}>Detail</th>
                <th style={{ width: '20%', textAlign: 'right' }}>Cost</th>
              </tr>
            </thead>
            <tbody>
              {employee.productBreakdown.map((prod, i) => (
                <tr key={i}>
                  <td>{prod.productName}</td>
                  <td>
                    <Badge variant="outline" className={badgeClass(prod.category === 'subscription' ? 'brand' : 'informative')}>
                      {prod.category}
                    </Badge>
                  </td>
                  <td style={{ color: 'hsl(var(--muted-foreground))' }}>{prod.detail || '-'}</td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatCurrency(prod.cost)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const emptyLineItem = (): CreateInvoiceLineItemRequest => ({
  productCode: '',
  description: '',
  quantity: 1,
  unitAmount: 0,
});

export function CreateInvoiceDialog({
  orgId,
  hasStripe,
  onCreated,
  onClose,
}: {
  orgId?: string;
  hasStripe: boolean;
  onCreated: () => void;
  onClose: () => void;
}) {
  const [lineItems, setLineItems] = useState<CreateInvoiceLineItemRequest[]>([emptyLineItem()]);
  const [notes, setNotes] = useState('');
  const [submitToStripe, setSubmitToStripe] = useState(hasStripe);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [orgIdInput, setOrgIdInput] = useState(orgId ?? '');

  const platformApiKey = (import.meta.env.VITE_PLATFORM_API_KEY as string) || '';

  const updateLineItem = (index: number, field: keyof CreateInvoiceLineItemRequest, value: string | number) => {
    setLineItems(prev => prev.map((li, i) => i === index ? { ...li, [field]: value } : li));
  };

  const removeLineItem = (index: number) => {
    if (lineItems.length <= 1) return;
    setLineItems(prev => prev.filter((_, i) => i !== index));
  };

  const runningTotal = lineItems.reduce((sum, li) => sum + li.quantity * li.unitAmount, 0);

  const canSubmit = orgIdInput.trim() !== ''
    && lineItems.every(li => li.productCode.trim() !== '' && li.description.trim() !== '' && li.quantity > 0 && li.unitAmount > 0)
    && platformApiKey !== '';

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await adminApi.createInvoice(platformApiKey, {
        organizationId: orgIdInput,
        notes: notes || undefined,
        submitToStripe,
        lineItems,
      });
      onCreated();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to create invoice';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open onOverlayClick={onClose}>
      <Modal.Content maxWidth="2xl" aria-labelledby="create-invoice-title">
        <Modal.Header className="px-6 py-4">
          <h2 id="create-invoice-title" className="text-lg font-semibold text-foreground">Create Invoice</h2>
        </Modal.Header>
        <Modal.Body className="px-6 py-4">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {!orgId && (
              <div>
                <label style={{ fontSize: '0.85rem', fontWeight: 600 }}>Organization ID</label>
                <Input
                  value={orgIdInput}
                  onChange={(e) => setOrgIdInput(e.target.value)}
                  placeholder="org_volato"
                />
              </div>
            )}

            <label style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: '-0.25rem' }}>Line Items</label>
            {lineItems.map((li, i) => (
              <div key={i} style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', border: '1px solid hsl(var(--border))', borderRadius: '6px', padding: '0.5rem' }}>
                <div style={{ flex: '1 1 120px' }}>
                  <label style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>Product Code</label>
                  <Input
                    value={li.productCode}
                    onChange={(e) => updateLineItem(i, 'productCode', e.target.value)}
                    placeholder="consulting"
                    className="h-8"
                  />
                </div>
                <div style={{ flex: '2 1 200px' }}>
                  <label style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>Description</label>
                  <Input
                    value={li.description}
                    onChange={(e) => updateLineItem(i, 'description', e.target.value)}
                    placeholder="Onboarding consulting - February"
                    className="h-8"
                  />
                </div>
                <div style={{ flex: '0 0 70px' }}>
                  <label style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>Qty</label>
                  <Input
                    type="number"
                    value={String(li.quantity)}
                    onChange={(e) => updateLineItem(i, 'quantity', parseFloat(e.target.value) || 0)}
                    className="h-8"
                  />
                </div>
                <div style={{ flex: '0 0 100px' }}>
                  <label style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>Unit ($)</label>
                  <Input
                    type="number"
                    value={String(li.unitAmount)}
                    onChange={(e) => updateLineItem(i, 'unitAmount', parseFloat(e.target.value) || 0)}
                    className="h-8"
                  />
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  disabled={lineItems.length <= 1}
                  onClick={() => removeLineItem(i)}
                  title="Remove line item"
                >
                  <X />
                </Button>
              </div>
            ))}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setLineItems(prev => [...prev, emptyLineItem()])}
              className="self-start"
            >
              <Plus />
              Add line item
            </Button>

            <div>
              <label style={{ fontSize: '0.85rem', fontWeight: 600 }}>Notes</label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional notes..."
                rows={2}
              />
            </div>

            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={submitToStripe}
                onCheckedChange={(c) => setSubmitToStripe(c === true)}
              />
              Submit to Stripe for payment
            </label>

            <div style={{ textAlign: 'right', fontWeight: 700, fontSize: '1.1rem' }}>
              Total: {formatCurrency(runningTotal)}
            </div>

            {error && (
              <div style={{ color: 'hsl(var(--destructive))', fontSize: '0.85rem' }}>{error}</div>
            )}
            {!platformApiKey && (
              <div style={{ color: 'hsl(var(--destructive))', fontSize: '0.85rem' }}>
                VITE_PLATFORM_API_KEY not configured — cannot create invoices.
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer className="flex justify-end gap-2 px-6 py-4">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit || submitting}
          >
            {submitting ? 'Creating...' : 'Create Invoice'}
          </Button>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
}

// ============================================================================
// Sales-operated portal actions (plan U8–U10): custom-deal create, on-behalf
// card collection, bonus token grants + a per-org grant audit list.
// ============================================================================

// The two PAID products closeable through the portal. Slugs MUST match the backend
// ProductRuleRegistry (EntitlementModels.ProductIds): salestrainer + crm. CRM is 0-day
// (charged on enable); SalesTrainer supports a genuine trial. Defaults to SalesTrainer (R11).
const DEAL_PRODUCTS: { id: string; label: string; supportsTrial: boolean }[] = [
  { id: 'salestrainer', label: 'SalesTrainer', supportsTrial: true },
  { id: 'crm', label: 'CRM', supportsTrial: false },
];

function SalesActionsCard({
  orgId,
  hasCardOnFile,
  cardCollectionReturnPath,
  onChanged,
}: {
  orgId: string;
  /**
   * Best-available card-on-file signal. We only have stripeCustomerId in the billing summary
   * (no explicit default-payment-method flag), so a paid deal's no-card block uses this as a
   * proxy; the backend authoritatively re-checks and a missing card surfaces inline as 402
   * collect_card_first.
   */
  hasCardOnFile: boolean;
  cardCollectionReturnPath: string;
  onChanged: () => void;
}) {
  const [showDealDialog, setShowDealDialog] = useState(false);
  const [showGrantDialog, setShowGrantDialog] = useState(false);
  const [collectingCard, setCollectingCard] = useState(false);
  const [collectCardError, setCollectCardError] = useState<string | null>(null);

  const [grants, setGrants] = useState<GrantHistoryItem[]>([]);
  const [grantsLoading, setGrantsLoading] = useState(true);
  const [grantsError, setGrantsError] = useState<string | null>(null);

  const fetchGrants = useCallback(async () => {
    setGrantsLoading(true);
    setGrantsError(null);
    try {
      const data = await adminApi.getGrantHistory(orgId);
      setGrants(data.grants ?? []);
    } catch (err: unknown) {
      setGrantsError(mapGenericError(err, 'Failed to load grant history'));
    } finally {
      setGrantsLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    fetchGrants();
  }, [fetchGrants]);

  const handleCollectCard = async () => {
    setCollectingCard(true);
    setCollectCardError(null);
    try {
      // returnPath MUST be a RELATIVE path starting with "/" — the backend prefixes it with
      // AdminDashboard:Url and appends org/card query params. Land the rep back on THIS org's
      // detail page (a real in-SPA route); OrgDetailPage reads the ?card= param to re-open the
      // Billing tab, which re-fetches on mount so the saved card / status is reflected.
      // (Passing the absolute window.location.href here would double the origin and 404.)
      const { url } = await adminApi.createAdminCardCollectionSession(
        orgId,
        cardCollectionReturnPath
      );
      window.location.href = url;
    } catch (err: unknown) {
      setCollectCardError(mapGenericError(err, 'Failed to start card collection'));
      setCollectingCard(false);
    }
  };

  return (
    <Card className="info-section">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
        <h3>Sales Actions</h3>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <Button size="sm" onClick={() => setShowDealDialog(true)}>
            <Plus />
            Create Custom Deal
          </Button>
          <Button size="sm" variant="secondary" disabled={collectingCard} onClick={handleCollectCard}>
            {collectingCard ? 'Opening…' : 'Collect Card'}
          </Button>
          <Button size="sm" variant="secondary" onClick={() => setShowGrantDialog(true)}>
            <Plus />
            Grant Tokens
          </Button>
        </div>
      </div>

      {collectCardError && (
        <div style={{ color: 'hsl(var(--destructive))', fontSize: '0.85rem', marginTop: '0.5rem' }}>{collectCardError}</div>
      )}
      {hasCardOnFile && (
        <span className="block" style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.8rem', marginTop: '0.4rem' }}>
          A Stripe customer is already on file. Re-collecting a card only sets the default if none is set yet.
        </span>
      )}

      {/* Per-org grant audit list (U10 / R14) */}
      <div style={{ marginTop: '1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontWeight: 600 }}>Token Grant History</span>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={fetchGrants} title="Refresh grant history">
            <RotateCw />
          </Button>
        </div>
        {grantsLoading ? (
          <span className="block" style={{ color: 'hsl(var(--muted-foreground))', marginTop: '0.5rem' }}>Loading grant history…</span>
        ) : grantsError ? (
          <div style={{ color: 'hsl(var(--destructive))', fontSize: '0.85rem', marginTop: '0.5rem' }}>{grantsError}</div>
        ) : grants.length > 0 ? (
          <div style={{ overflowX: 'auto', marginTop: '0.5rem' }}>
            <table className="admin-table">
              <thead>
                <tr>
                  <th style={{ width: '20%' }}>Date</th>
                  <th style={{ width: '20%', textAlign: 'right' }}>Amount</th>
                  <th style={{ width: '20%', textAlign: 'right' }}>Balance After</th>
                  <th style={{ width: '25%' }}>Reason</th>
                  <th style={{ width: '15%' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {grants.map((g, i) => (
                  <tr key={g.idempotencyKey ?? `${g.createdAt}-${i}`}>
                    <td style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.85rem' }}>{formatDate(g.createdAt)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>
                      +{formatNumber(g.amount)}
                      <span style={{ color: 'hsl(var(--muted-foreground))', fontWeight: 400, fontSize: '0.8rem' }}> ({formatTokenCount(g.amount)})</span>
                    </td>
                    <td style={{ textAlign: 'right' }}>{formatNumber(g.balanceAfter)}</td>
                    <td>{g.reason || '-'}</td>
                    <td>{g.status || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <span className="block" style={{ color: 'hsl(var(--muted-foreground))', marginTop: '0.5rem' }}>No token grants for this org yet.</span>
        )}
      </div>

      {showDealDialog && (
        <CreateCustomDealDialog
          orgId={orgId}
          hasCardOnFile={hasCardOnFile}
          onCreated={() => { setShowDealDialog(false); onChanged(); fetchGrants(); }}
          onClose={() => setShowDealDialog(false)}
        />
      )}
      {showGrantDialog && (
        <GrantTokensDialog
          orgId={orgId}
          onGranted={() => { setShowGrantDialog(false); onChanged(); fetchGrants(); }}
          onClose={() => setShowGrantDialog(false)}
        />
      )}
    </Card>
  );
}

/**
 * Trial-length input after a fee edit. Moving from a paid fee to $0 sets the trial to 0: the 7-day
 * default belongs to paid deals, and a $0 deal with a trial ENDS at trial end (the server revokes a
 * $0 trial instead of rolling it into free service), so a giveaway must never silently become a
 * 7-day trial. The paid trial length is remembered and restored when the fee goes back above $0 —
 * clearing the field to retype a price passes through $0, and that must not silently turn a paid
 * trial deal into charge-immediately. A trial length staff typed while at $0 is their choice and
 * is kept.
 */
export function trialAfterFeeChange(
  previousFee: number,
  nextFeeInput: string,
  trialInput: string,
  savedPaidTrial: string | null,
): { trialInput: string; savedPaidTrial: string | null } {
  const nextFee = Math.max(0, parseFloat(nextFeeInput) || 0);
  if (previousFee > 0 && nextFee <= 0) return { trialInput: '0', savedPaidTrial: trialInput };
  if (previousFee <= 0 && nextFee > 0) {
    const untouched = savedPaidTrial !== null && trialInput === '0';
    return { trialInput: untouched ? savedPaidTrial : trialInput, savedPaidTrial: null };
  }
  return { trialInput, savedPaidTrial };
}

/** Disclosure for a $0 (giveaway) monthly deal; a $0 deal with a trial is a time-boxed free trial. */
export function zeroPriceDealDisclosure(trialDays: number, tokenLabel: string): string {
  return trialDays > 0
    ? `Free ${trialDays}-day trial — $0, no card required. Access ends when the trial ends; after that the customer sees the paywall until they add a paid plan. If this org has had this product before, the trial is skipped and the $0 deal stays active with no end date.`
    : `Free giveaway — $0/mo, no charge and no card required. ${tokenLabel} tokens granted each cycle.`;
}

/** Success message for a $0 deal, by enable outcome; null for outcomes the paid copy already covers. */
export function zeroPriceDealSuccessMessage(outcome: string, trialDays: number, trialEndsAt: string | null): string | null {
  switch (outcome) {
    case 'TrialStarted':
      return `Free trial started — no card needed. Access ends ${trialEndsAt ? formatDate(trialEndsAt) : 'at trial end'} unless the customer adds a paid plan.`;
    case 'ChargedAndActivated':
      return trialDays > 0
        ? 'Free giveaway active with no end date — this org had this product before, so the trial was skipped. Use Cancel now if that is not what you intended.'
        : 'Free giveaway active — $0, no charge.';
    default:
      return null;
  }
}

export function CreateCustomDealDialog({
  orgId,
  hasCardOnFile,
  onCreated,
  onClose,
}: {
  orgId: string;
  hasCardOnFile: boolean;
  onCreated: () => void;
  onClose: () => void;
}) {
  const [productId, setProductId] = useState('salestrainer');
  const [feeInput, setFeeInput] = useState('750');
  const [tokensInput, setTokensInput] = useState('10000000');
  const [trialInput, setTrialInput] = useState('7');
  // The paid trial length set aside while the fee reads $0 (see trialAfterFeeChange).
  const [savedPaidTrial, setSavedPaidTrial] = useState<string | null>(null);
  const [termInput, setTermInput] = useState('1');
  // The rep's raw "collect full term upfront" intent. Kept independent of fee so a valid prepaid
  // deal survives an in-flight fee edit; the effective prepaid decision (isPrepaid) also requires
  // fee > 0.
  const [upfront, setUpfront] = useState(false);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<AdminCreateDealResponse | null>(null);
  // ONE idempotency token per dialog session so a double-click (or an idempotent retry of a
  // failed submit) cannot double-charge. A fresh dialog (success closes it) mints a fresh token.
  const [idempotencyToken] = useState(() => crypto.randomUUID());

  const product = DEAL_PRODUCTS.find(p => p.id === productId) ?? DEAL_PRODUCTS[0];
  // Clamp to non-negative: a negative fee would slip past the isGiveaway (fee<=0) branch,
  // skip the card-missing check (fee>0 false), and be sent as a negative monthlyPriceUsd.
  // A negative input collapses to 0 → treated as a giveaway.
  const fee = Math.max(0, parseFloat(feeInput) || 0);
  const tokens = parseInt(tokensInput, 10) || 0;

  // Term must be a whole number of months, 1..120 (120 = form sanity, not a business cap). Number()
  // (not parseInt) so "1.5" fails Number.isInteger rather than silently truncating to 1.
  const termMonths = Number(termInput);
  const termValid = Number.isInteger(termMonths) && termMonths >= 1 && termMonths <= 120;

  // Effective prepaid requires a price. The toggle auto-reverts to off the moment the fee hits $0
  // (see the fee input's onChange), so `upfront` is never true at $0 — no stuck invalid state.
  const isPrepaid = upfront && fee > 0;
  // Nothing to prepay at $0 → the toggle is disabled (and already reverted off by then).
  const upfrontDisabled = fee <= 0;

  // Trial is hidden entirely while the upfront toggle is on (prepaid charges at creation, no trial);
  // CRM is 0-day (charged on enable) so it never shows a trial field either.
  const showTrial = product.supportsTrial && !upfront;
  const trialDays = showTrial ? (parseInt(trialInput, 10) || 0) : 0;

  // The full prepay total charged at creation — always monthly rate × term (no lump-sum entry).
  const prepaidTotal = computePrepaidTotal(fee, termMonths);

  const isGiveaway = fee <= 0;
  const cardMissing = fee > 0 && !hasCardOnFile;
  // A $0 giveaway must carry a reason (audit trail); a paid deal's reason is optional.
  const reasonRequired = isGiveaway;

  // Clearing the fee to $0 reverts the upfront toggle so it can never sit "on" over a $0 price, and
  // resets the paid-deal trial default (see trialInputAfterFeeChange).
  const handleFeeChange = (value: string) => {
    const next = trialAfterFeeChange(fee, value, trialInput, savedPaidTrial);
    setTrialInput(next.trialInput);
    setSavedPaidTrial(next.savedPaidTrial);
    setFeeInput(value);
    if ((Math.max(0, parseFloat(value) || 0)) <= 0) setUpfront(false);
  };

  const disclosure = (() => {
    const feeLabel = formatCurrency(fee);
    if (isPrepaid) {
      // A displayed total is only ever computed from a valid whole-number term. While the term field
      // is invalid (empty / 0 / negative / decimal / >120) submit is already blocked; show a neutral
      // prompt instead of a total derived from the raw/fallback term (which could self-contradict —
      // e.g. a total for 999 months next to "covers 1 months").
      if (!termValid) {
        return `Charges the full term to the card on file immediately, then continues month-to-month at ${feeLabel}/mo. Enter a valid term length (1–120 months) to see the total.`;
      }
      const totalLabel = formatCurrency(prepaidTotal);
      return `Charges ${totalLabel} to the card on file immediately — covers ${termMonths} months at ${feeLabel}/mo, grants ${formatTokenCount(tokens)} tokens each month on the anniversary, then continues month-to-month at ${feeLabel}/mo.`;
    }
    // Monthly collection: the existing three cases, wording untouched so term=1 reads exactly as
    // today; a multi-month monthly term appends one plain sentence about the commitment.
    let base: string;
    if (isGiveaway) {
      // A $0 deal with a trial is a time-boxed free trial: at trial end the server revokes it and
      // the customer sees the paywall (ProductSubscriptionBillingService, zero_price_trial_ended).
      base = zeroPriceDealDisclosure(trialDays, formatTokenCount(tokens));
    } else if (!product.supportsTrial) {
      base = `Charged immediately on enable — the card on file is charged ${feeLabel} now, then ${feeLabel}/mo until disabled. No free trial.`;
    } else if (trialDays > 0) {
      base = `Genuine ${trialDays}-day free trial — no charge now; the card on file is first charged ${feeLabel} at trial end, then ${feeLabel}/mo.`;
    } else {
      base = `Charged immediately on enable — the card on file is charged ${feeLabel} now, then ${feeLabel}/mo.`;
    }
    if (termValid && termMonths > 1 && !(isGiveaway && trialDays > 0)) {
      base += isGiveaway
        ? ` Recorded on a ${termMonths}-month term.`
        : ` Committed to a ${termMonths}-month term, billed monthly.`;
    }
    return base;
  })();

  const canSubmit = !submitting
    && !cardMissing
    && tokens >= 0
    && termValid
    && (!reasonRequired || reason.trim() !== '');

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await adminApi.createCustomDeal(orgId, {
        productId,
        monthlyPriceUsd: fee,
        monthlyTokenAllotment: tokens,
        trialLengthDays: trialDays,
        termMonths,
        collectionMode: isPrepaid ? 'prepaid' : 'monthly',
        requestIdempotencyToken: idempotencyToken,
        reason: reason.trim() || undefined,
      });
      // Hold the dialog open on success to show the outcome (incl. the returning-org "charged now,
      // no trial" signal and the prepaid charged total) before the rep dismisses it.
      setResult(res);
    } catch (err: unknown) {
      setError(mapDealError(err));
    } finally {
      setSubmitting(false);
    }
  };

  // Outcome → plain-language confirmation. A prepaid deal always charges at creation; surface the
  // exact total charged. ChargedAndActivated when the rep ASKED for a trial (trialDays > 0) means a
  // returning org with a prior paid relationship was charged immediately and skipped the trial.
  const successMessage = (res: AdminCreateDealResponse): string => {
    if (res.collectionMode?.toLowerCase() === 'prepaid') {
      const amt = res.chargedAmountUsd != null ? formatCurrency(res.chargedAmountUsd) : 'the full term';
      return `Prepaid term active — the card on file was charged ${amt} for the full term. Tokens grant monthly on the anniversary, then billing continues month-to-month.`;
    }
    const zeroPriceMessage = fee <= 0 ? zeroPriceDealSuccessMessage(res.outcome, trialDays, res.trialEndsAt) : null;
    if (zeroPriceMessage) return zeroPriceMessage;
    switch (res.outcome) {
      case 'TrialStarted':
        return 'Trial started — no charge now. The card on file will be charged at trial end.';
      case 'ChargedAndActivated':
        return trialDays > 0
          ? 'Charged now — returning org, no trial. The card on file was charged immediately and the subscription is active.'
          : 'Charged and activated — the card on file was charged and the subscription is now active.';
      case 'AlreadyActive':
        return 'Already active — this org already had a live subscription for this product, so no new charge was made.';
      default:
        return `Deal created (${res.outcome}).`;
    }
  };

  return (
    <Modal open onOverlayClick={() => { if (result) onCreated(); else onClose(); }}>
      <Modal.Content maxWidth="xl" aria-labelledby="create-deal-title">
        <Modal.Header className="px-6 py-4">
          <h2 id="create-deal-title" className="text-lg font-semibold text-foreground">Create Custom Deal</h2>
        </Modal.Header>
        <Modal.Body className="px-6 py-4">
          {result ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <div style={{ background: 'hsl(var(--success) / 0.1)', border: '1px solid hsl(var(--success) / 0.3)', borderRadius: '6px', padding: '0.75rem', fontSize: '0.9rem', color: 'hsl(var(--success))' }}>
                {successMessage(result)}
              </div>
              <div style={{ fontSize: '0.8rem', color: 'hsl(var(--muted-foreground))', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                {result.chargedAmountUsd != null && result.chargedAmountUsd > 0 && (
                  <span>Charged: {formatCurrency(result.chargedAmountUsd)}</span>
                )}
                {result.committedThroughDate && (
                  <span>Term through: {formatDate(result.committedThroughDate)}</span>
                )}
                {result.subscriptionId && (
                  <span>Subscription: <code>{result.subscriptionId}</code></span>
                )}
                {result.latestStripePaymentIntentId && (
                  <span>Payment intent: <code>{result.latestStripePaymentIntentId}</code></span>
                )}
                {result.trialEndsAt && (
                  <span>Trial ends: {formatDate(result.trialEndsAt)}</span>
                )}
              </div>
            </div>
          ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
            <div>
              <label style={{ fontSize: '0.85rem', fontWeight: 600 }}>Product</label>
              <Select
                value={productId}
                onValueChange={(v) => setProductId(v)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DEAL_PRODUCTS.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label style={{ fontSize: '0.85rem', fontWeight: 600 }}>Monthly fee</label>
              <div className="relative">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
                <Input
                  type="number"
                  value={feeInput}
                  onChange={(e) => handleFeeChange(e.target.value)}
                  className="pl-7"
                />
              </div>
              {isGiveaway && (
                <div style={{ fontSize: '0.78rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.2rem' }}>
                  $0 = giveaway (no charge, no card required). A reason is required below.
                </div>
              )}
            </div>

            <div>
              <label style={{ fontSize: '0.85rem', fontWeight: 600 }}>Term length</label>
              <div className="relative">
                <Input
                  type="number"
                  min={1}
                  max={120}
                  step={1}
                  value={termInput}
                  onChange={(e) => setTermInput(e.target.value)}
                  className="pr-16"
                />
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">months</span>
              </div>
              {!termValid && (
                <div style={{ fontSize: '0.78rem', color: 'hsl(var(--destructive))', marginTop: '0.2rem' }}>
                  Enter a whole number of months between 1 and 120.
                </div>
              )}
            </div>

            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem' }}>
              <Switch
                id="collect-upfront"
                checked={upfront}
                disabled={upfrontDisabled}
                onCheckedChange={(v) => setUpfront(v === true)}
              />
              <label htmlFor="collect-upfront" style={{ fontSize: '0.85rem', lineHeight: 1.3 }}>
                <span style={{ fontWeight: 600 }}>Collect full term upfront</span>
                <span style={{ display: 'block', color: 'hsl(var(--muted-foreground))', fontSize: '0.78rem' }}>
                  Off = billed monthly at the rate above. On = the card on file is charged the whole
                  term now (no trial); tokens still grant monthly.
                </span>
              </label>
            </div>

            <div>
              <label style={{ fontSize: '0.85rem', fontWeight: 600 }}>Monthly token allotment</label>
              <div className="relative">
                <Input
                  type="number"
                  value={tokensInput}
                  onChange={(e) => setTokensInput(e.target.value)}
                  className="pr-16"
                />
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">tokens</span>
              </div>
              <div style={{ fontSize: '0.8rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.2rem' }}>= {formatTokenCount(tokens)}</div>
            </div>

            {showTrial && (
              <div>
                <label style={{ fontSize: '0.85rem', fontWeight: 600 }}>Trial length</label>
                <div className="relative">
                  <Input
                    type="number"
                    value={trialInput}
                    onChange={(e) => setTrialInput(e.target.value)}
                    className="pr-12"
                  />
                  <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">days</span>
                </div>
              </div>
            )}

            <div>
              <label style={{ fontSize: '0.85rem', fontWeight: 600 }}>
                Reason{reasonRequired ? '' : ' (optional)'}
              </label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={reasonRequired ? 'Why is this a $0 giveaway?' : 'Optional note for the deal-authorship audit…'}
                rows={2}
              />
            </div>

            <div style={{ background: 'hsl(var(--secondary))', border: '1px solid hsl(var(--border))', borderRadius: '6px', padding: '0.6rem 0.75rem', fontSize: '0.82rem', color: 'hsl(var(--foreground))' }}>
              {disclosure}
            </div>

            {cardMissing && (
              <div style={{ color: 'hsl(var(--destructive))', fontSize: '0.85rem' }}>
                No card on file. Use "Collect Card" first, then create the deal.
              </div>
            )}
            {error && (
              <div style={{ color: 'hsl(var(--destructive))', fontSize: '0.85rem' }}>{error}</div>
            )}
          </div>
          )}
        </Modal.Body>
        <Modal.Footer className="flex justify-end gap-2 px-6 py-4">
          {result ? (
            <Button onClick={onCreated}>Done</Button>
          ) : (
            <>
              <Button variant="secondary" onClick={onClose}>Cancel</Button>
              <Button onClick={handleSubmit} disabled={!canSubmit}>
                {submitting ? 'Creating…' : 'Create Deal'}
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
}

export function GrantTokensDialog({
  orgId,
  onGranted,
  onClose,
}: {
  orgId: string;
  onGranted: () => void;
  onClose: () => void;
}) {
  const [tokensInput, setTokensInput] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // ONE idempotency key per dialog session (not per click/render) so a double-click cannot
  // double-grant; the backend dedupes on this key. Success closes the dialog → next grant is fresh.
  const [idempotencyKey] = useState(() => crypto.randomUUID());

  const tokens = parseInt(tokensInput, 10) || 0;
  const canSubmit = !submitting && tokens > 0 && reason.trim() !== '';

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await adminApi.grantTokens(orgId, {
        tokenAmount: tokens,
        reason: reason.trim(),
        idempotencyKey,
      });
      onGranted();
    } catch (err: unknown) {
      setError(mapGenericError(err, 'Failed to grant tokens'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open onOverlayClick={onClose}>
      <Modal.Content maxWidth="lg" aria-labelledby="grant-tokens-title">
        <Modal.Header className="px-6 py-4">
          <h2 id="grant-tokens-title" className="text-lg font-semibold text-foreground">Grant Tokens</h2>
        </Modal.Header>
        <Modal.Body className="px-6 py-4">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
            <div>
              <label style={{ fontSize: '0.85rem', fontWeight: 600 }}>Tokens to grant</label>
              <div className="relative">
                <Input
                  type="number"
                  value={tokensInput}
                  onChange={(e) => setTokensInput(e.target.value)}
                  placeholder="5000000"
                  className="pr-16"
                />
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">tokens</span>
              </div>
              <div style={{ fontSize: '0.8rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.2rem' }}>= {formatTokenCount(tokens)}</div>
            </div>

            <div>
              <label style={{ fontSize: '0.85rem', fontWeight: 600 }}>Reason</label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Why are these bonus tokens being granted?"
                rows={2}
              />
            </div>

            {error && (
              <div style={{ color: 'hsl(var(--destructive))', fontSize: '0.85rem' }}>{error}</div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer className="flex justify-end gap-2 px-6 py-4">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {submitting ? 'Granting…' : 'Grant Tokens'}
          </Button>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
}

function InvoicesSection({
  invoices,
  allInvoices,
  statusFilter,
  onStatusFilterChange,
  orgId,
  hasStripe,
  onInvoiceCreated,
}: {
  invoices: BillingInvoiceSummary[];
  allInvoices: BillingInvoiceSummary[];
  statusFilter: string;
  onStatusFilterChange: (v: string) => void;
  orgId: string;
  hasStripe: boolean;
  onInvoiceCreated: () => void;
}) {
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const statuses = ['All', ...Array.from(new Set(allInvoices.map(i => i.status)))];

  return (
    <Card className="info-section">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3>Invoices</h3>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <Button
            size="sm"
            onClick={() => setShowCreateDialog(true)}
          >
            <Plus />
            Create Invoice
          </Button>
          <Select value={statusFilter} onValueChange={onStatusFilterChange}>
            <SelectTrigger className="h-8 min-w-[110px] text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {statuses.map(s => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      {showCreateDialog && (
        <CreateInvoiceDialog
          orgId={orgId}
          hasStripe={hasStripe}
          onCreated={() => { setShowCreateDialog(false); onInvoiceCreated(); }}
          onClose={() => setShowCreateDialog(false)}
        />
      )}
      {invoices.length > 0 ? (
        <div style={{ overflowX: 'auto', marginTop: '0.75rem' }}>
          <table className="admin-table">
            <thead>
              <tr>
                <th style={{ width: '20%' }}>Invoice #</th>
                <th style={{ width: '15%' }}>Period</th>
                <th style={{ width: '20%', textAlign: 'right' }}>Amount</th>
                <th style={{ width: '15%' }}>Status</th>
                <th style={{ width: '15%' }}>Paid</th>
                <th style={{ width: '15%' }}>Stripe</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map(inv => (
                <tr key={inv.id}>
                  <td style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{inv.number}</td>
                  <td style={{ color: 'hsl(var(--muted-foreground))' }}>{inv.billingPeriod}</td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatCurrency(inv.totalAmount)}</td>
                  <td>
                    <Badge variant="outline" className={badgeClass(getStatusBadgeColor(inv.status))}>
                      {inv.status}
                    </Badge>
                  </td>
                  <td style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.85rem' }}>{formatDate(inv.paidAt)}</td>
                  <td>
                    {inv.channelReferenceId && (
                      <a
                        href={`https://dashboard.stripe.com/invoices/${inv.channelReferenceId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ fontSize: '0.8rem' }}
                      >
                        <ExternalLink style={{ width: 14, height: 14, verticalAlign: 'middle' }} /> View
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <span className="block" style={{ color: 'hsl(var(--muted-foreground))', marginTop: '0.5rem' }}>No invoices found for this period</span>
      )}
    </Card>
  );
}
